import { FC } from "react";

import { TAB_QUERY_PARAM } from "components/constants";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { PageListItem } from "components/materials/topic/activity/detail/PageListItem";
import { SectionPageCategory, SectionPageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { useOrdered } from "mds/hooks/use-ordered";
import { useAppSelector } from "store/index";
import {
  selectAllPagesWithDueDates,
  selectCurrentSectionId,
  selectIsEditModeEnabled,
  selectPagesForCurrentSection,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { PAGE_LIST_CUTOFF_LENGTH, usePageActions } from "utils/page";
import { Link } from "utils/routing";
import { CONTENT_SIDEBAR_TAB_NAME, SectionPageCategoryToSidebarTab, pageUrl } from "utils/urls";

type SectionPageListProps = {
  category: SectionPageCategory;
  shouldTruncateList?: boolean;
  canEdit?: boolean;
};

export const SectionPageList: FC<SectionPageListProps> = ({
  category,
  shouldTruncateList,
  canEdit,
}) => {
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const sectionPages = useListSelector((s) =>
    selectPagesForCurrentSection(s, category),
  ) as SectionPageType[];
  const orderedPages = useOrdered(sectionPages);
  const [pages, setPages] = useLocalCopy<SectionPageType[]>(orderedPages);

  const { createPage, savePage, navigateToPage } = usePageActions();

  const pagesWithDueDates = useListSelector(selectAllPagesWithDueDates);

  const movePagesLocally = useDnDItemOrdering(pages, setPages, "category");

  const onDropPage = (
    droppedPage: SectionPageType,
    _dropIndex: number,
    sourceContainerId: string,
  ) => {
    if (sourceContainerId !== droppedPage.category) {
      // If the item was dropped in a different container, we need to clean up afterwards
      // by removing it from the original container
      setPages((prevPages) => prevPages.filter((page) => page.id !== droppedPage.id));
    }
    savePage({ id: droppedPage.id, category: droppedPage.category, order: droppedPage.order });
  };

  const onCreatePage = () => createPage(category);

  const isAssignment = category === "assignment";

  let shouldShowOverflowMenu;
  if (isAssignment) {
    shouldShowOverflowMenu =
      pagesWithDueDates.filter((page) => page.category !== "assignment").length > 0 ||
      (shouldTruncateList && pages.length > PAGE_LIST_CUTOFF_LENGTH);
  } else {
    shouldShowOverflowMenu = shouldTruncateList && pages.length > PAGE_LIST_CUTOFF_LENGTH;
  }
  const visiblePages =
    !shouldShowOverflowMenu || (isAssignment && !shouldTruncateList)
      ? pages
      : pages.slice(0, PAGE_LIST_CUTOFF_LENGTH);

  let pageCount = pages.length;
  if (isAssignment) {
    const assignmentsWithoutDueDates = pages.filter((page) => !page.due_at);
    pageCount = pagesWithDueDates.length + assignmentsWithoutDueDates.length;
  }

  if (!currentSectionId) {
    return null;
  }

  if (!visiblePages.length && !canEdit) {
    return null;
  }

  const sidebarTabForCategory = SectionPageCategoryToSidebarTab[
    category
  ] as CONTENT_SIDEBAR_TAB_NAME;

  return (
    <div className="mt-8" role="list">
      <h3 className="mb-2 flex items-center justify-between text-black-tint-10">
        {t(`page_variant_plural.${category}`)}
      </h3>

      {!visiblePages.length && !isEditModeEnabled && (
        <div className="text-black-tint-55">{t(`section_view.empty_${category}`)}</div>
      )}

      {visiblePages.map((page, index) => (
        <PageListItem
          containerId={category}
          key={page.id}
          kind="primary"
          lastItem={pages.length - 1 === index}
          page={page}
          pageIndex={index}
          onDropPage={onDropPage}
          onMovePageLocally={movePagesLocally}
          onPageClick={() =>
            navigateToPage(
              page,
              pageUrl(currentSectionId, page.id, {
                [TAB_QUERY_PARAM]: sidebarTabForCategory,
              }),
            )
          }
        />
      ))}

      {canEdit && (
        <HideUnlessEditModeButton
          className="mt-1 h-[42px]"
          eventName={`Section - Add ${sidebarTabForCategory}`}
          reference={category}
          onClick={onCreatePage}
        />
      )}

      {shouldShowOverflowMenu && pages.length > 0 && (
        <Button
          as={Link}
          className="mt-4"
          eventName={`Course Overview - ${sidebarTabForCategory} / View All`}
          kind="gray"
          size="s"
          title={t(`content_view.view_all.${category}`, { count: pageCount })}
          to={pageUrl(currentSectionId, pages[0].id, {
            [TAB_QUERY_PARAM]: sidebarTabForCategory,
          })}
        >
          {t(`content_view.view_all.${category}`, { count: pageCount })}
        </Button>
      )}
    </div>
  );
};
